import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getAllProjects,
    getProjectById,
    getProjectsByCategory,
    getFeaturedProjects,
    createProject,
    updateProject,
    deleteProject,
    uploadProjectImage,
    deleteProjectImage,
    Project
} from '../firebase/projectService';

// Query keys
export const projectKeys = {
    all: ['projects'] as const,
    lists: () => [...projectKeys.all, 'list'] as const,
    list: (filters: string) => [...projectKeys.lists(), { filters }] as const,
    featured: () => [...projectKeys.all, 'featured'] as const,
    categories: () => [...projectKeys.all, 'categories'] as const,
    category: (category: string) => [...projectKeys.categories(), category] as const,
    details: () => [...projectKeys.all, 'detail'] as const,
    detail: (id: string) => [...projectKeys.details(), id] as const,
};

// Hooks
export const useProjectsQuery = () => {
    return useQuery({
        queryKey: projectKeys.lists(),
        queryFn: getAllProjects,
    });
};

export const useProjectQuery = (id: string) => {
    return useQuery({
        queryKey: projectKeys.detail(id),
        queryFn: () => getProjectById(id),
        enabled: !!id,
    });
};

export const useProjectsByCategoryQuery = (category: string) => {
    return useQuery({
        queryKey: projectKeys.category(category),
        queryFn: () => getProjectsByCategory(category),
        enabled: !!category,
    });
};

export const useFeaturedProjectsQuery = () => {
    return useQuery({
        queryKey: projectKeys.featured(),
        queryFn: getFeaturedProjects,
    });
};

export const useCreateProjectMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) =>
            createProject(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: projectKeys.featured() });
        },
    });
};

export const useUpdateProjectMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: {
            id: string;
            data: Partial<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>>
        }) => updateProject(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: projectKeys.featured() });

            // If category was updated, invalidate category queries
            if (variables.data.category) {
                queryClient.invalidateQueries({ queryKey: projectKeys.categories() });
            }
        },
    });
};

export const useDeleteProjectMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteProject(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: projectKeys.featured() });
            queryClient.invalidateQueries({ queryKey: projectKeys.categories() });
        },
    });
};

export const useUploadProjectImageMutation = () => {
    return useMutation({
        mutationFn: ({
            file,
            projectId,
            isGallery = false,
            fileName
        }: {
            file: File;
            projectId: string;
            isGallery?: boolean;
            fileName?: string;
        }) => uploadProjectImage(file, projectId, isGallery, fileName),
    });
};

export const useDeleteProjectImageMutation = () => {
    return useMutation({
        mutationFn: (imageUrl: string) => deleteProjectImage(imageUrl),
    });
}; 